import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Trash2, Instagram } from 'lucide-react';

interface Reel {
  id: string;
  url: string;
  position: number;
}

const isValidReelUrl = (url: string) =>
  /^https?:\/\/(www\.)?instagram\.com\/(p|reel|reels|tv)\/[^/?#]+/i.test(url.trim());

export default function AdminReels() {
  const [rows, setRows] = useState<Reel[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data, error } = await supabase
      .from('instagram_reels')
      .select('*')
      .order('position', { ascending: true });
    if (error) toast.error('שגיאה בטעינה');
    setRows((data as Reel[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const url = newUrl.trim();
    if (!isValidReelUrl(url)) {
      toast.error('יש להזין קישור תקין ל-Instagram');
      return;
    }
    setBusy(true);
    const maxPos = rows.reduce((m, r) => Math.max(m, r.position), 0);
    const { error } = await supabase
      .from('instagram_reels')
      .insert({ url, position: maxPos + 10 });
    setBusy(false);
    if (error) return toast.error('הוספה נכשלה: ' + error.message);
    toast.success('נוסף');
    setNewUrl('');
    load();
  };

  const remove = async (id: string) => {
    if (!confirm('למחוק את הריל?')) return;
    const { error } = await supabase.from('instagram_reels').delete().eq('id', id);
    if (error) return toast.error('מחיקה נכשלה');
    toast.success('נמחק');
    load();
  };

  const updateUrl = async (id: string, url: string) => {
    if (!isValidReelUrl(url)) {
      toast.error('קישור לא תקין');
      return;
    }
    const { error } = await supabase.from('instagram_reels').update({ url }).eq('id', id);
    if (error) return toast.error('עדכון נכשל');
    toast.success('נשמר');
  };

  const swap = async (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= rows.length) return;
    const a = rows[index];
    const b = rows[j];
    const next = [...rows];
    next[index] = { ...a, position: b.position };
    next[j] = { ...b, position: a.position };
    setRows(next.sort((x, y) => x.position - y.position));
    const { error: e1 } = await supabase
      .from('instagram_reels')
      .update({ position: b.position })
      .eq('id', a.id);
    const { error: e2 } = await supabase
      .from('instagram_reels')
      .update({ position: a.position })
      .eq('id', b.id);
    if (e1 || e2) {
      toast.error('שינוי הסדר נכשל');
      load();
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">טוען…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Instagram className="h-5 w-5" /> רגעים מהקליניקה
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          ניהול הסרטונים מהאינסטגרם המוצגים בקרוסלה בעמוד הבית. ניתן להוסיף, לערוך, למחוק ולשנות את הסדר.
        </p>
      </div>

      <div className="border rounded-md p-4 bg-white">
        <div className="text-sm font-medium mb-2">הוספת ריל חדש</div>
        <div className="flex gap-2">
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://www.instagram.com/p/XXXX/"
            dir="ltr"
          />
          <Button onClick={add} disabled={busy} className="gap-1">
            <Plus className="h-4 w-4" /> הוספה
          </Button>
        </div>
      </div>

      <div className="border rounded-md bg-white divide-y">
        {rows.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground text-center">אין רילים עדיין</div>
        )}
        {rows.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2 p-3">
            <div className="flex flex-col">
              <button
                className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                onClick={() => swap(i, -1)}
                disabled={i === 0}
                aria-label="הזז למעלה"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                onClick={() => swap(i, 1)}
                disabled={i === rows.length - 1}
                aria-label="הזז למטה"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>
            <div className="w-8 text-xs text-muted-foreground text-center">{i + 1}</div>
            <Input
              defaultValue={r.url}
              dir="ltr"
              className="flex-1 font-mono text-xs"
              onBlur={(e) => {
                if (e.target.value.trim() !== r.url) updateUrl(r.id, e.target.value.trim());
              }}
            />
            <a
              href={r.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:underline"
            >
              פתח
            </a>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => remove(r.id)}
              aria-label="מחק"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
